import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import SettingsDialog from "@/components/SettingsDialog";
import ArchitectureFlow from "@/components/ArchitectureFlow";
import ActiveProxyCard from "@/components/ActiveProxyCard";
import IPPool from "@/components/pool/IPPool";
import ProxyTools from "@/components/ProxyTools";
import UsageHistory from "@/components/UsageHistory";
import UsageGuide from "@/components/UsageGuide";
import PortBindings from "@/components/PortBindings";
import ProxyNodes from "@/components/ProxyNodes";
import { Button } from "@/components/ui/button";
import { Settings2, Wallet, LogOut, ShieldCheck, User, Sun, Moon, Network } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { AUTH } from "@/constants/testIds";

const NAV = [
  { id: "pool-section", label: "PROXY" },
  { id: "ports-section", label: "PORTS" },
  { id: "nodes-section", label: "NODES", adminOnly: true },
  { id: "tools-section", label: "TOOLS" },
  { id: "history-section", label: "HISTORY" },
];

export default function Dashboard() {
  const { user, isAdmin, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [balance, setBalance] = useState(null);
  const [gateway, setGateway] = useState(null);

  const loadGateway = useCallback(async () => {
    try {
      const { data } = await api.get("/gateway/status");
      setGateway(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadGateway();
    const t = setInterval(loadGateway, 5000);
    return () => clearInterval(t);
  }, [loadGateway]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setConfigured(data.configured);
      if (!data.configured && isAdmin) setSettingsOpen(true);
    } catch (e) {}
  }, [isAdmin]);

  const loadActive = useCallback(async () => {
    try {
      const { data } = await api.get("/proxy/active");
      setActive(data.active);
    } catch (e) {}
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/history");
      setHistory(data || []);
    } catch (e) {}
  }, []);

  const loadBalance = useCallback(async () => {
    try {
      const { data } = await api.get("/account/balance");
      setBalance(data);
    } catch (e) {
      setBalance(null);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadActive();
    loadHistory();
    loadBalance();
  }, [loadSettings, loadActive, loadHistory, loadBalance]);

  const onActivated = () => {
    loadActive();
    loadHistory();
  };

  const gb = balance?.proxy?.rotating?.total?.traffic?.availableGb;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* NSOCKS-style top navbar */}
      <header className="sticky top-0 z-40 bg-panel/90 backdrop-blur-md border-b border-line">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 h-14 flex items-center gap-4">
          {/* brand */}
          <div className="flex items-center gap-2 shrink-0" data-testid="logo">
            <div className="w-8 h-8 grid place-items-center rounded-md bg-neon text-white">
              <Network size={17} />
            </div>
            <span className="font-heading text-lg tracking-tight">789<span className="text-neon">PROXY</span></span>
          </div>

          {/* nav links */}
          <nav className="hidden md:flex items-center gap-1 ml-2">
            {NAV.filter((n) => !n.adminOnly || isAdmin).map((n, i) => (
              <button
                key={n.id}
                onClick={() => scrollTo(n.id)}
                className={`px-3 py-1.5 rounded-md font-mono text-[11px] tracking-wide transition-colors ${
                  i === 0 ? "text-neon bg-accent" : "text-dim hover:text-foreground hover:bg-accent"
                }`}
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </button>
            ))}
            <a
              href="https://floppydata.com/docs"
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-md font-mono text-[11px] tracking-wide text-dim hover:text-foreground hover:bg-accent transition-colors"
            >
              SUPPORT
            </a>
          </nav>

          {/* right cluster */}
          <div className="flex items-center gap-2 ml-auto">
            <div
              className="hidden sm:flex items-center gap-1.5 rounded-full border border-line bg-accent/60 px-3 py-1.5 font-mono text-xs"
              data-testid="balance-pill"
              title="Remaining traffic quota"
            >
              <Wallet size={13} className="text-laser" />
              <span className="text-laser font-semibold">{gb != null ? `${gb.toFixed(2)} GB` : "— GB"}</span>
            </div>

            <div className="flex items-center gap-1.5 pl-1" title={user?.email}>
              <span className="w-7 h-7 rounded-full grid place-items-center bg-accent text-neon">
                {isAdmin ? <ShieldCheck size={14} /> : <User size={14} />}
              </span>
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-xs font-medium">{user?.name || user?.email?.split("@")[0]}</span>
                <span className="overline !text-[9px]" data-testid={AUTH.roleBadge}>
                  <span data-testid={AUTH.userEmail}>{user?.role}</span>
                </span>
              </div>
            </div>

            <button
              onClick={toggle}
              className="w-8 h-8 grid place-items-center rounded-md border border-line text-dim hover:text-neon hover:border-neon transition-colors"
              title={isDark ? "Light mode" : "Dark mode"}
              data-testid="theme-toggle-btn"
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {isAdmin && (
              <Button
                onClick={() => setSettingsOpen(true)}
                variant="outline"
                size="sm"
                className="rounded-md border-line hover:border-neon hover:text-neon font-mono text-xs h-8 gap-1.5"
                data-testid="topbar-settings-btn"
              >
                <Settings2 size={14} /> <span className="hidden lg:inline">API Key</span>
              </Button>
            )}

            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="rounded-md border-line hover:border-signal hover:text-signal font-mono text-xs h-8 gap-1.5"
              data-testid="logout-button"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Row 1: active + architecture */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <ActiveProxyCard active={active} />
          </div>
          <div className="lg:col-span-8">
            <ArchitectureFlow active={active} gateway={gateway} />
          </div>
        </div>

        {/* Row 2: IP pool marketplace */}
        <div id="pool-section" className="scroll-mt-20">
          <IPPool active={active} onActivated={onActivated} isAdmin={isAdmin} />
        </div>

        {/* Row 2b: SOCKS5 port forwarding */}
        <div id="ports-section" className="scroll-mt-20">
          <PortBindings isAdmin={isAdmin} />
        </div>

        {/* Row 2c: self-hosted nodes */}
        {isAdmin && (
          <div id="nodes-section" className="scroll-mt-20">
            <ProxyNodes isAdmin={isAdmin} />
          </div>
        )}

        {/* Row 3: tools + guide */}
        <div id="tools-section" className="scroll-mt-20 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <ProxyTools active={active} gateway={gateway} />
          </div>
          <div className="lg:col-span-8">
            <UsageGuide active={active} gateway={gateway} />
          </div>
        </div>

        {/* Row 4: history */}
        <div id="history-section" className="scroll-mt-20">
          <UsageHistory items={history} onChanged={onActivated} isAdmin={isAdmin} />
        </div>

        <footer className="pt-2 pb-6 text-center">
          <span className="font-mono text-[10px] text-dim tracking-widest uppercase">
            789PROXY · Proxy Marketplace &amp; Gateway
          </span>
        </footer>
      </div>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={() => {
          loadSettings();
          loadBalance();
        }}
      />
    </div>
  );
}
